/**
 * Панель «ближайшие публичные укрытия» (City Resilience Hack, 24.09.2026):
 * общая для баннера городской тревоги (directory-alert.tsx) и кнопки
 * «Укрытие» в SOS на главной (sos-bar.tsx) — чтобы не дублировать запрос
 * геопозиции, список и карту в двух местах.
 *
 * Геопозиция никуда не уходит, кроме запроса маршрута нашему же серверу
 * (/api/route-ext, как в use-external-escape.ts).
 *
 * Без геопозиции (отказ/нет) — список считаем от примерного центра Таллинна:
 * это приближение «от центра карты» из задачи (пункт 6), т.к. на главной нет
 * постоянно открытой карты, от которой можно бы взять реальный центр.
 */
import type { LonLat } from '@app/geo'
import { formatDistance, tr } from '@app/i18n'
import { useEffect, useRef, useState } from 'preact/hooks'
import { ensureGeoStart } from '../emergency/locate'
import {
  loadPublicShelters,
  nearestPublicShelters,
  type PublicShelter,
  type PublicSheltersData,
} from '../public-shelters'
import { fetchExternalLeg } from '../route-splice'
import { geoStart } from '../state'
import { type AlertMap, createAlertMap } from './directory-alert-map'
import { buildShelterRoute } from './shelter-route'

/** Центр Таллинна — приближение «центра карты», когда позиции нет. */
const FALLBACK_CENTER: LonLat = [24.7536, 59.437]

export function PublicSheltersPanel() {
  const [geo, setGeo] = useState<LonLat | null>(geoStart.value)
  const [geoDenied, setGeoDenied] = useState(false)
  const [data, setData] = useState<PublicSheltersData | null>(null)
  const [selected, setSelected] = useState<PublicShelter | null>(null)
  const [route, setRoute] = useState<ReturnType<typeof buildShelterRoute>>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<AlertMap | null>(null)

  useEffect(() => {
    void loadPublicShelters().then(setData)
  }, [])

  useEffect(() => {
    if (geo) return
    void ensureGeoStart().then((p) => {
      if (p) setGeo(p)
      else setGeoDenied(true)
    })
  }, [geo])

  const referencePoint = geo ?? FALLBACK_CENTER
  const nearest = data ? nearestPublicShelters(data, referencePoint, 3) : []

  useEffect(() => {
    if (!selected && nearest[0]) setSelected(nearest[0].shelter)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- выбираем один раз, как появились данные
  }, [nearest[0]?.shelter.id])

  useEffect(() => {
    if (!geo || !selected) {
      setRoute(buildShelterRoute(geo, selected?.point ?? null, null))
      return
    }
    let alive = true
    void fetchExternalLeg(geo, selected.point).then((leg) => {
      if (alive) setRoute(buildShelterRoute(geo, selected.point, leg))
    })
    return () => {
      alive = false
    }
  }, [geo, selected])

  useEffect(() => {
    const box = boxRef.current
    if (!box) return
    let alive = true
    void createAlertMap(box, { you: geo, shelter: selected?.point ?? null, route }).then((m) => {
      if (alive) mapRef.current = m
      else m.destroy()
    })
    return () => {
      alive = false
      mapRef.current?.destroy()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- карта создаётся один раз
  }, [])

  useEffect(() => {
    mapRef.current?.setState({ you: geo, shelter: selected?.point ?? null, route })
  }, [geo, selected, route])

  return (
    <div class="sh-panel">
      {!geo && (
        <p class="sh-geo-hint">
          {geoDenied ? tr('shelters.geo_denied') : tr('shelters.geo_asking')}
        </p>
      )}
      <div ref={boxRef} class="sh-map" />
      {route?.straight && selected && (
        <p class="sh-straight-note">{tr('shelters.straight_line')}</p>
      )}
      <ul class="sh-list">
        {nearest.length === 0 && data && <li class="sh-empty">{tr('shelters.none')}</li>}
        {nearest.map(({ shelter, distanceM }) => (
          <li key={shelter.id} class={`sh-item${selected?.id === shelter.id ? ' sh-item-on' : ''}`}>
            <span class="sh-item-name">{shelter.name}</span>
            {shelter.address && <span class="sh-item-addr">{shelter.address}</span>}
            <span class="sh-item-dist">{formatDistance(distanceM)}</span>
            <button type="button" class="sh-item-btn" onClick={() => setSelected(shelter)}>
              {tr('shelters.route_btn')}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
